"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Download, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import {
  ThemeBackground,
  ThemeCard,
  ThemeText,
  ThemeSection,
  AuroraBlobs
} from "@/components/ui/theme-utils";
import {
  PrimaryButton,
  SecondaryButton,
  FloatingThemeToggle
} from "@/components/ui/buttons";
import { Navbar } from "@/components/ui/navbar";
import { Footer } from "@/components/ui/footer";

type DownloadOption = {
  id: string;
  title: string;
  description: string;
  icon: string;
  platforms: Platform[];
};

type Platform = {
  name: string;
  url?: string;
  onClick?: () => void;
  chipVersion?: string;
};
/**
   * Downloads the latest macOS DMG file from the appcast.xml
   */
const downloadLatestDMG = async (): Promise<void> => {
  try {
    const response = await fetch("https://raw.githubusercontent.com/rzset/dist/refs/heads/main/appcast.xml");
    if (!response.ok) {
      alert("Failed to fetch the appcast.xml.");
      return;
    }

    const xmlText = await response.text().then(text => text.trim());

    // Ensure we're in a browser environment before using DOMParser
    if (typeof window === 'undefined') {
      throw new Error('DOMParser can only be used in browser environment');
    }
    
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "application/xml");

    // Check for parsing errors
    if (xmlDoc.getElementsByTagName("parsererror").length > 0) {
      console.error("Error parsing XML:", xmlText);
      alert("Failed to parse appcast.xml.");
      return;
    }

    console.log("Parsing XML...");

    // Get all <item> elements
    const items = xmlDoc.getElementsByTagName("item");
    if (items.length === 0) {
      alert("No releases found in the appcast.xml.");
      return;
    }

    console.log("Processing releases...");

    // Array to hold DMG builds
    const dmgFiles: Array<{ url: string; buildNum: number }> = [];

    // Iterate over each <item>
    Array.from(items).forEach(item => {
      const enclosure = item.getElementsByTagName("enclosure")[0];

      if (enclosure) {
        const osAttribute = enclosure.getAttribute("sparkle:os");

        // If sparkle:os is missing, "none", or null, treat as a DMG build
        if (!osAttribute || osAttribute.toLowerCase() === "none") {
          // Get the URL
          const url = enclosure.getAttribute("url");

          // Get the build number from <sparkle:version>
          const versionElement = item.getElementsByTagName("sparkle:version")[0];
          const buildNum = versionElement
            ? parseInt(versionElement.textContent?.trim() || "0", 10)
            : 0;

          console.log(`Found DMG: ${url} - Build: ${buildNum}`);

          if (url) {
            dmgFiles.push({ url, buildNum });
          }
        }
      }
    });

    console.log("Available DMG builds:", dmgFiles);

    if (dmgFiles.length === 0) {
      alert("No DMG setup files found.");
      return;
    }

    // Sort by build number in descending order
    dmgFiles.sort((a, b) => b.buildNum - a.buildNum);

    // Get the highest-numbered DMG build
    const latestDMGBuild = dmgFiles[0];

    console.log("Redirecting to latest DMG build:", latestDMGBuild.url);
    window.location.href = latestDMGBuild.url;
  } catch (error) {
    console.error("Error fetching or parsing appcast.xml:", error);
    alert("Failed to get the latest DMG build.");
  }
};

/**
 * Downloads the latest Windows build from the appcast.xml
 */
const downloadLatestWindowsBuild = async (): Promise<void> => {
  try {
    const response = await fetch("https://raw.githubusercontent.com/rzset/dist/refs/heads/main/appcast.xml");
    if (!response.ok) {
      alert("Failed to fetch the appcast.xml.");
      return;
    }

    const xmlText = await response.text().then(text => text.trim());

    // Ensure we're in a browser environment before using DOMParser
    if (typeof window === 'undefined') {
      throw new Error('DOMParser can only be used in browser environment');
    }
    
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "application/xml");

    // Check for parsing errors
    if (xmlDoc.getElementsByTagName("parsererror").length > 0) {
      console.error("Error parsing XML:", xmlText);
      alert("Failed to parse appcast.xml.");
      return;
    }

    console.log("Parsing XML...");

    // Get all <item> elements
    const items = xmlDoc.getElementsByTagName("item");
    if (items.length === 0) {
      alert("No releases found in the appcast.xml.");
      return;
    }

    console.log("Processing releases...");

    // Array to hold Windows builds
    const winFiles: Array<{ url: string; version: string; buildNum: number }> = [];

    // Iterate over each <item>
    Array.from(items).forEach(item => {
      const enclosure = item.getElementsByTagName("enclosure")[0];

      if (enclosure) {
        const isWindows = enclosure.getAttribute("sparkle:os");
        if (isWindows === "windows") {
          // Get the URL
          const url = enclosure.getAttribute("url");

          // Get the version from its sibling <sparkle:version>
          const versionElement = item.getElementsByTagName("sparkle:version")[0];
          const version = versionElement ? versionElement.textContent?.trim() || "" : "";

          // Extract build number (e.g., from "2.4.6+246")
          const match = version.match(/\+(\d+)$/);
          const buildNum = match ? parseInt(match[1], 10) : 0;

          console.log(`Found: ${url} - Version: ${version} - Build: ${buildNum}`);

          if (url) {
            winFiles.push({ url, version, buildNum });
          }
        }
      }
    });

    console.log("Available Windows builds:", winFiles);

    if (winFiles.length === 0) {
      alert("No Windows setup files found.");
      return;
    }

    // Sort by build number in descending order
    winFiles.sort((a, b) => b.buildNum - a.buildNum);

    // Get the highest-numbered Windows build
    const latestWindowsBuild = winFiles[0];

    console.log("Redirecting to latest build:", latestWindowsBuild.url);
    window.location.href = latestWindowsBuild.url;
  } catch (error) {
    console.error("Error fetching or parsing appcast.xml:", error);
    alert("Failed to get the latest Windows build.");
  }
};

export default function DownloadsPage() {
  const [isLoading, setIsLoading] = useState<{macos: boolean, windows: boolean}>({ macos: false, windows: false });
  const { isDark } = useTheme();
  
  // Enhanced download functions with loading state that lasts at least 1 second
  const handleMacDownload = async () => {
    setIsLoading(prev => ({ ...prev, macos: true }));
    const startTime = Date.now();
    
    try {
      await downloadLatestDMG();
    } catch (error) {
      console.error("Error downloading macOS app:", error);
    } finally {
      // Ensure loading state shows for at least 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      setIsLoading(prev => ({ ...prev, macos: false }));
    }
  };
  
  const handleWindowsDownload = async () => {
    setIsLoading(prev => ({ ...prev, windows: true }));
    const startTime = Date.now();
    
    try {
      await downloadLatestWindowsBuild();
    } catch (error) {
      console.error("Error downloading Windows app:", error);
    } finally {
      // Ensure loading state shows for at least 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      setIsLoading(prev => ({ ...prev, windows: false }));
    }
  };

  const downloadOptions: DownloadOption[] = [
    {
      id: "macos",
      title: "macOS",
      description: "Download for macOS 10.15 or later",
      icon: "apple",
      platforms: [
        { name: "Download for macOS", onClick: handleMacDownload }
      ]
    },
    {
      id: "windows",
      title: "Windows",
      description: "Download for Windows 10/11 (64-bit)",
      icon: "microsoft",
      platforms: [
        { name: "Download for Windows", onClick: handleWindowsDownload }
      ]
    }
  ];

  return (
    <ThemeBackground className="min-h-screen">
      {/* Navigation - using the reusable Navbar component */}
      <Navbar
        showDownloadButton={false}
        isHomePage={false}
      />

      {/* Hero Section */}
      <section className="relative overflow-x-clip overflow-y-visible pt-36 sm:pt-44 pb-16">
        <div
          className={cn(
            "absolute inset-0 -z-10",
            isDark ? "bg-mesh-dark" : "bg-mesh-light"
          )}
        />
        <div
          className={cn(
            "absolute inset-0 -z-10 opacity-40",
            "bg-grid"
          )}
        />
        <AuroraBlobs variant="mixed" />

        <div className="container mx-auto px-5 sm:px-6 relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Download <span className="text-gradient">Zset</span>
            </h1>
            <ThemeText variant="subtle" className="text-xl mb-8">
              Get started with Zset and experience blazing-fast file transfers.
            </ThemeText>
          </motion.div>
        </div>
      </section>

      {/* Downloads Section */}
      <ThemeSection className="mt-[-2rem]">
        <div className="container mx-auto px-5 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto justify-items-center">
            {downloadOptions.map((option, index) => (
              <motion.div
                key={option.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="w-full max-w-sm"
              >
                <ThemeCard hover className="h-full flex flex-col p-6 sm:p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10">
                      <img
                        src={`/images/theme/${option.icon}-light.svg`}
                        alt={`${option.title} logo`}
                        className="w-7 h-7"
                      />
                    </div>
                    <h3 className="text-xl font-bold">{option.title}</h3>
                  </div>
                  <ThemeText variant="subtle" className="mb-6">{option.description}</ThemeText>

                  <div className="mt-auto flex flex-col space-y-3">
                    {option.platforms.map((platform, i) => {
                      const isButtonLoading = option.id === "macos" ? isLoading.macos : isLoading.windows;
                      
                      return (
                        <button
                          key={i}
                          onClick={platform.onClick}
                          disabled={isButtonLoading}
                          className={cn(
                            "flex items-center justify-between w-full px-4 py-3 rounded-xl transition-colors text-left",
                            "border",
                            isDark
                              ? "bg-white/[0.04] hover:bg-white/[0.08] border-white/10 hover:border-white/20"
                              : "bg-white/80 hover:bg-white border-stone-200 hover:border-stone-300",
                            isButtonLoading && "opacity-70 cursor-not-allowed"
                          )}
                        >
                          <div className="flex items-center">
                            {isButtonLoading ? (
                              <div className="h-5 w-5 border-2 border-t-transparent border-brand-500 rounded-full animate-spin mr-3"></div>
                            ) : (
                              <Download className="h-5 w-5 text-brand-500 mr-3" />
                            )}
                            <span>{isButtonLoading ? "Preparing download..." : platform.name}</span>
                          </div>
                          {platform.chipVersion && (
                            <span className={cn(
                              "text-xs px-2 py-1 rounded",
                              isDark ? "bg-white/10" : "bg-stone-200"
                            )}>
                              {platform.chipVersion}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </ThemeCard>
              </motion.div>
            ))}
          </div>
        </div>
      </ThemeSection>

      {/* Instructions Section */}
      <ThemeSection variant="alternate">
        <div className="container mx-auto px-5 sm:px-6 max-w-4xl">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">Installation Instructions</h2>
            <ThemeText variant="subtle" className="text-xl">
              Follow these simple steps to get Zset up and running on your system.
            </ThemeText>
          </motion.div>

          <ThemeCard className="mb-8 p-6 sm:p-8">
            <h3 className="text-2xl font-bold mb-4">macOS</h3>
            <ol className="space-y-4 mb-6">
              <li className="flex">
                <div className="w-6 h-6 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 text-xs font-semibold">
                  1
                </div>
                <ThemeText>Download the package for your Mac.</ThemeText>
              </li>
              <li className="flex">
                <div className="w-6 h-6 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 text-xs font-semibold">
                  2
                </div>
                <ThemeText>Open the downloaded .dmg file.</ThemeText>
              </li>
              <li className="flex">
                <div className="w-6 h-6 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 text-xs font-semibold">
                  3
                </div>
                <ThemeText>Drag Zset to your Applications folder.</ThemeText>
              </li>
              <li className="flex">
                <div className="w-6 h-6 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 text-xs font-semibold">
                  4
                </div>
                <ThemeText>Launch Zset from your Applications folder or Launchpad.</ThemeText>
              </li>
            </ol>
          </ThemeCard>

          <ThemeCard className="mb-8 p-6 sm:p-8">
            <h3 className="text-2xl font-bold mb-4">Windows</h3>
            <ol className="space-y-4 mb-6">
              <li className="flex">
                <div className="w-6 h-6 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 text-xs font-semibold">
                  1
                </div>
                <ThemeText>Download the Windows installer.</ThemeText>
              </li>
              <li className="flex">
                <div className="w-6 h-6 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 text-xs font-semibold">
                  2
                </div>
                <ThemeText>Double-click the downloaded file to begin installation.</ThemeText>
              </li>
              <li className="flex">
                <div className="w-6 h-6 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 text-xs font-semibold">
                  3
                </div>
                <ThemeText>Follow the on-screen instructions to complete the installation.</ThemeText>
              </li>
              <li className="flex">
                <div className="w-6 h-6 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-0.5 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 text-xs font-semibold">
                  4
                </div>
                <ThemeText>Launch Zset from your Start menu or desktop shortcut.</ThemeText>
              </li>
            </ol>
          </ThemeCard>
        </div>
      </ThemeSection>

      {/* System Requirements */}
      <ThemeSection>
        <div className="container mx-auto px-5 sm:px-6 max-w-4xl">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">System Requirements</h2>
            <ThemeText variant="subtle" className="text-xl">
              Make sure your system meets these requirements for optimal performance.
            </ThemeText>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <ThemeCard className="p-6 sm:p-8">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center mr-4 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10">
                  <img
                    src={`/images/theme/apple-light.svg`}
                    alt="macOS logo"
                    className="w-5 h-5"
                  />
                </div>
                macOS
              </h3>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full mr-2 bg-brand-500"></div>
                  <ThemeText variant="subtle">macOS 11.0 or later</ThemeText>
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full mr-2 bg-brand-500"></div>
                  <ThemeText variant="subtle">Intel or Apple Silicon processor (Universal)</ThemeText>
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full mr-2 bg-brand-500"></div>
                  <ThemeText variant="subtle">4 GB RAM minimum</ThemeText>
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full mr-2 bg-brand-500"></div>
                  <ThemeText variant="subtle">500 MB free disk space</ThemeText>
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full mr-2 bg-brand-500"></div>
                  <ThemeText variant="subtle">Internet connection</ThemeText>
                </li>
              </ul>
            </ThemeCard>

            <ThemeCard className="p-6 sm:p-8">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center mr-4 bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10">
                  <img
                    src={`/images/theme/microsoft-light.svg`}
                    alt="Windows logo"
                    className="w-5 h-5"
                  />
                </div>
                Windows
              </h3>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full mr-2 bg-brand-500"></div>
                  <ThemeText variant="subtle">Windows 10 or Windows 11</ThemeText>
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full mr-2 bg-brand-500"></div>
                  <ThemeText variant="subtle">64-bit processor</ThemeText>
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full mr-2 bg-brand-500"></div>
                  <ThemeText variant="subtle">4 GB RAM minimum</ThemeText>
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full mr-2 bg-brand-500"></div>
                  <ThemeText variant="subtle">500 MB free disk space</ThemeText>
                </li>
                <li className="flex items-center">
                  <div className="w-1.5 h-1.5 rounded-full mr-2 bg-brand-500"></div>
                  <ThemeText variant="subtle">Internet connection</ThemeText>
                </li>
              </ul>
            </ThemeCard>
          </div>
        </div>
      </ThemeSection>

      {/* CTA Section */}
      <ThemeSection variant="alternate">
        <div className="container mx-auto px-5 sm:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
          >
            <ThemeCard className="p-12 rounded-2xl text-center max-w-4xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">Need Help with Installation?</h2>
              <ThemeText variant="subtle" className="text-xl mb-8 max-w-2xl mx-auto">
                Our support team is ready to help you get started. Contact us for personalized assistance.
              </ThemeText>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <PrimaryButton
                  href="mailto:support@zset.in"
                  icon={<ArrowRight className="h-5 w-5" />}
                >
                  Contact Support
                </PrimaryButton>
                <SecondaryButton
                  href="/"
                  icon={<ArrowRight className="h-5 w-5" />}
                >
                  Return to Home
                </SecondaryButton>
              </div>
            </ThemeCard>
          </motion.div>
        </div>
      </ThemeSection>

      {/* Footer */}
      <Footer isHomePage={false} />

      {/* Floating Theme Toggle Button */}
      <FloatingThemeToggle />
    </ThemeBackground>
  );
}